import { useState, useRef, useEffect } from 'react'
import { Logger } from '@/services/Logger'
import { useUserRoles } from '@/contexts/UserRolesContext'
import Navbar from '@/components/Navbar'
import HeaderBar from '@/components/HeaderBar'
import DateInput from '@/components/DateInput'
import './BulkUploadDevices.css'
import { Helmet } from 'react-helmet-async'
import { PAGES } from '@/constants/pages'
import { createTemplate, parseTemplate, isExcelFileType, getIndexesForType } from '@/utils/spreadsheetHelpers'
import { MESSAGES } from '@/constants/messages'
import { BULK_UPLOAD_TEMPLATE, ColumnDataType } from '@/constants/templateSchema'
import { BulkUploadDevicesResponse, DeviceService, DeviceUploadOptionsResponse } from '@/services/DeviceService'

type DeviceRow = {
  simSerialNumber: string
  phoneNumber: string
  simProvider: string
  deviceSerialNumber: string
  deviceImeiNumber: string
  deviceType: string
  channelPartner: string
  issueDate: string
}

type RowError = {
  simSerialNumber?: string
  phoneNumber?: string
  simProvider?: string
  deviceSerialNumber?: string
  deviceImeiNumber?: string
  deviceType?: string
  channelPartner?: string
  issueDate?: string
}

export default function BulkUploadDevicesPage() {
  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
  const [uploadError, setUploadError] = useState<string>('')
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragActive, setIsDragActive] = useState(false)

  // Data rows and validation state
  const [rows, setRows] = useState<DeviceRow[]>([])
  const [rowErrors, setRowErrors] = useState<RowError[]>([])
  const [validOptions, setValidOptions] = useState<DeviceUploadOptionsResponse | null>(null)

  // Processing state
  const [processingStatus, setProcessingStatus] = useState<'idle' | 'processing' | 'completed' | 'error'>('idle')
  const [processingResults, setProcessingResults] = useState<BulkUploadDevicesResponse | null>(null)
  const [processingError, setProcessingError] = useState<string>('')

  // Bulk apply state
  const [bulkSimProvider, setBulkSimProvider] = useState<string>('')
  const [bulkDeviceType, setBulkDeviceType] = useState<string>('')
  const [bulkChannelPartner, setBulkChannelPartner] = useState<string>('')

  const { loading: rolesLoading } = useUserRoles()

  // Fetch valid options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const options = await DeviceService.getUploadOptions()
        setValidOptions(options)
        Logger.info('BulkUploadDevicesPage: Loaded valid options', {
          channelPartners: options.partners.length,
          deviceTypes: options.deviceTypes.length,
          simProviders: options.simProviders.length
        })
      } catch (error) {
        Logger.error('BulkUploadDevicesPage: Failed to load valid options', { error: (error as Error).message })
      }
    }
    fetchOptions()
  }, [])

  // Run validation whenever rows change
  useEffect(() => {
    if (rows.length > 0) {
      validateAllRows()
    }
  }, [rows, validOptions])

  // =============================================================================
  // CSV Parsing
  // =============================================================================

  const parseCsvLine = (line: string): string[] => {
    const columns: string[] = []
    let inQuotes = false
    let currentColumn = ''

    for (let i = 0; i < line.length; i++) {
      const c = line[i]

      if (c === '"') {
        inQuotes = !inQuotes
      } else if (c === ',' && !inQuotes) {
        columns.push(currentColumn.trim())
        currentColumn = ''
      } else {
        currentColumn += c
      }
    }

    columns.push(currentColumn.trim())
    return columns
  }

  // =============================================================================
  // Validation
  // =============================================================================

  const validateRow = (row: DeviceRow, index: number): RowError => {
    const errors: RowError = {}

    // Check mandatory fields
    if (!row.deviceSerialNumber) {
      errors.deviceSerialNumber = 'Required field'
    }
    if (!row.deviceImeiNumber) {
      errors.deviceImeiNumber = 'Required field'
    }
    if (!row.deviceType) {
      errors.deviceType = 'Required field'
    }
    if (!row.channelPartner) {
      errors.channelPartner = 'Required field'
    }
    if (!row.issueDate) {
      errors.issueDate = 'Required field'
    }

    // Validate date format
    if (row.issueDate && !isValidDate(row.issueDate)) {
      errors.issueDate = 'Invalid date format'
    }

    // Check if values match valid options
    if (validOptions) {
      if (row.channelPartner && !validOptions.partners.includes(row.channelPartner)) {
        errors.channelPartner = 'Invalid channel partner'
      }
      if (row.deviceType && !validOptions.deviceTypes.includes(row.deviceType)) {
        errors.deviceType = 'Not in list (you can still add new types)'
      }
      if (row.simProvider && !validOptions.simProviders.includes(row.simProvider)) {
        errors.simProvider = 'Not in list (you can still add new providers)'
      }
    }

    // Check for duplicates within the uploaded data
    const duplicateSerial = rows.findIndex((r, i) => i !== index && r.deviceSerialNumber.toLowerCase() === row.deviceSerialNumber.toLowerCase())
    if (duplicateSerial !== -1) {
      errors.deviceSerialNumber = `Duplicate (row ${duplicateSerial + 2})`
    }

    const duplicateImei = rows.findIndex((r, i) => i !== index && r.deviceImeiNumber.toLowerCase() === row.deviceImeiNumber.toLowerCase())
    if (duplicateImei !== -1) {
      errors.deviceImeiNumber = `Duplicate (row ${duplicateImei + 2})`
    }

    // Check for duplicate SIM serial numbers if not empty
    if (row.simSerialNumber) {
      const duplicateSimSerial = rows.findIndex((r, i) => i !== index && r.simSerialNumber && r.simSerialNumber.toLowerCase() === row.simSerialNumber.toLowerCase())
      if (duplicateSimSerial !== -1) {
        errors.simSerialNumber = `Duplicate (row ${duplicateSimSerial + 2})`
      }
    }

    // Check for duplicate phone numbers if not empty
    if (row.phoneNumber) {
      const duplicatePhone = rows.findIndex((r, i) => i !== index && r.phoneNumber && r.phoneNumber.toLowerCase() === row.phoneNumber.toLowerCase())
      if (duplicatePhone !== -1) {
        errors.phoneNumber = `Duplicate (row ${duplicatePhone + 2})`
      }
    }

    return errors
  }

  const parseUKDate = (dateInput: any): Date | null => {
    if (!dateInput) return null;

    if (dateInput instanceof Date) {
        return isNaN(dateInput.getTime()) ? null : dateInput;
    }

    const dateString = String(dateInput);

    const ukFormatMatch = dateString.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (ukFormatMatch) {
      const [, day, month, year] = ukFormatMatch;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(date.getTime())) return date;
    }

    const nativeDate = new Date(dateString);
    if (!isNaN(nativeDate.getTime())) {
      return nativeDate;
    }

    return null;
  }

  const isValidDate = (dateString: string): boolean => {
    return parseUKDate(dateString) !== null
  }

  const formatDateToUK = (dateString: string): string => {
    if (!dateString) return ''
    const date = parseUKDate(dateString)
    if (!date) return dateString
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}/${month}/${year}`
  }

  const formatDateToISO = (ukDateString: string): string => {
    if (!ukDateString) return ''
    const date = parseUKDate(ukDateString)
    if (!date) return ukDateString
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  const applyBulkSimProvider = () => {
    if (!bulkSimProvider) return
    const newRows = rows.map(row => ({ ...row, simProvider: bulkSimProvider }))
    setRows(newRows)
  }

  const applyBulkDeviceType = () => {
    if (!bulkDeviceType) return
    const newRows = rows.map(row => ({ ...row, deviceType: bulkDeviceType }))
    setRows(newRows)
  }

  const applyBulkChannelPartner = () => {
    if (!bulkChannelPartner) return
    const newRows = rows.map(row => ({ ...row, channelPartner: bulkChannelPartner }))
    setRows(newRows)
  }

  const validateAllRows = () => {
    const errors = rows.map((row, index) => validateRow(row, index))
    setRowErrors(errors)
    return errors
  }

  const hasErrors = (): boolean => {
    return rowErrors.some(error => Object.keys(error).length > 0)
  }

  // =============================================================================
  // File Selection and Drag/Drop
  // =============================================================================

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await isExcelFileType(file)
    if (!result) {
      setUploadError(MESSAGES.ERRORS.FILE.INVALID_TYPE)
      return
    }

    setSelectedFile(file)
    setUploadError('')
    setUploadStatus('idle')
    setRows([])
    setRowErrors([])

    Logger.info('BulkUploadDevicesPage: Excel file selected', { fileName: file.name, fileSize: file.size })
  }

  const handleDropZoneFiles = async (fileList: FileList | null | undefined) => {
    const file = fileList?.[0]
    if (!file) return

    const result = await isExcelFileType(file)
    if (!result) {
      setUploadError(MESSAGES.ERRORS.FILE.INVALID_TYPE)
      return
    }

    setSelectedFile(file)
    setUploadError('')
    setUploadStatus('idle')
    setRows([])
    setRowErrors([])
    Logger.info('BulkUploadDevicesPage: Excel file selected (drag-drop)', { fileName: file.name, fileSize: file.size })
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isDragActive) setIsDragActive(true)
  }

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(true)
  }

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragActive(false)
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      handleDropZoneFiles(e.dataTransfer.files)
      e.dataTransfer.clearData()
    }
  }

  // =============================================================================
  // File Upload and Processing
  // =============================================================================

  const handleUpload = async () => {
    if (!selectedFile) return

    setUploadStatus('uploading')
    setUploadError('')

    try {
      const template = await parseTemplate(selectedFile, BULK_UPLOAD_TEMPLATE.columns)
      if (!template.success) {
        setUploadStatus('error')
        setUploadError(template.error)
        return
      }
      
      const deviceRows: DeviceRow[] = []
      const indexes = getIndexesForType(BULK_UPLOAD_TEMPLATE.columns, ColumnDataType.Date)
      template.data.rows.forEach(row => {
        indexes.forEach(index => {
          var date = row[index]
          if (date) {
            deviceRows.push({
              simSerialNumber: row[0],
              phoneNumber: row[1],
              simProvider: row[2],
              deviceSerialNumber: row[3],
              deviceImeiNumber: row[4],
              deviceType: row[5],
              channelPartner: row[6],
              issueDate: formatDateToUK(date)
            })
          }
        });
      });

      setRows(deviceRows)
      setUploadStatus('success')

    } catch (error) {
      setUploadStatus('error')
      setUploadError(MESSAGES.ERRORS.FILE.EXCEPTION)
    }
  }

  const downloadTemplate = () => {
    createTemplate(BULK_UPLOAD_TEMPLATE)
  }

  // =============================================================================
  // Row Editing
  // =============================================================================

  const updateCell = (rowIndex: number, field: keyof DeviceRow, value: string) => {
    const newRows = [...rows]
    newRows[rowIndex][field] = value
    setRows(newRows)
  }

  const convertRowsToCSV = (): string => {
    const header = BULK_UPLOAD_TEMPLATE.columns.map(column => column.header).join(',')
    const dataRows = rows.map(row =>
      [
        row.simSerialNumber,
        row.phoneNumber,
        row.simProvider,
        row.deviceSerialNumber,
        row.deviceImeiNumber,
        row.deviceType,
        row.channelPartner,
        row.issueDate
      ].join(',')
    )
    return [header, ...dataRows].join('\n')
  }

  // =============================================================================
  // Device Processing
  // =============================================================================

  const handleProcessDevices = async () => {
    if (hasErrors()) {
      setUploadError('Please fix all errors before processing')
      return
    }

    setProcessingStatus('processing')
    setProcessingError('')
    setProcessingResults(null)

    try {
      Logger.info('BulkUploadDevicesPage: Starting device processing')
      const csvContent = convertRowsToCSV()
      const results = await DeviceService.bulkCreate(csvContent)
      setProcessingResults(results)

      if (results.success) {
        setProcessingStatus('completed')
        Logger.info('BulkUploadDevicesPage: Device processing completed successfully', {
          totalRows: results.totalRows
        })
      } else {
        setProcessingStatus('error')
        setProcessingError(results.errorMessage || 'An error occurred while processing devices')
        Logger.warn('BulkUploadDevicesPage: Device processing failed', {
          errorMessage: results.errorMessage,
          failedRow: results.failedRow,
          totalRows: results.totalRows
        })
      }
    } catch (error) {
      setProcessingStatus('error')
      setProcessingError((error as Error).message || 'An error occurred while processing devices')
      Logger.error('BulkUploadDevicesPage: Device processing exception', { error: (error as Error).message })
    }
  }

  const resetUpload = () => {
    setSelectedFile(null)
    setUploadStatus('idle')
    setUploadError('')
    setRows([])
    setRowErrors([])
    setProcessingStatus('idle')
    setProcessingResults(null)
    setProcessingError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // =============================================================================
  // Render
  // =============================================================================

  if (rolesLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className="page-container">
      <Helmet>
        <title>{PAGES.BULK_UPLOAD_DEVICES.title}</title>
      </Helmet>
      <Navbar />
      <main className="page-main-content">
        <HeaderBar />
        <div className="page-content">
          <h2>Bulk Upload Devices</h2>

          <div className="bulk-upload-container">
            <div className="upload-description">
              <p>
                Bulk upload devices using an Excel file containing all required device information.
                You can download an example{' '}
                <button
                  className="template-link"
                  onClick={downloadTemplate}
                >
                  Excel file here
                </button>.
              </p>
            </div>

            {/* Show drag and drop only when not showing the table */}
            {(uploadStatus === 'idle' || uploadStatus === 'error' || uploadStatus === 'uploading') && (
              <div
                className={`upload-section${isDragActive ? ' drag-active' : ''}`}
                onDragOver={onDragOver}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
              >
                {uploadStatus === 'idle' || uploadStatus === 'error' ? (
                  <>
                    <div className="file-input-container">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx"
                        onChange={handleFileSelect}
                        className="file-input"
                        id="csv-file-input"
                      />
                      <label htmlFor="csv-file-input" className="file-input-label">
                        Choose File
                      </label>
                      <div className="drop-hint">or drag and drop an Excel file here</div>
                      {selectedFile && (
                        <span className="selected-file-name">{selectedFile.name}</span>
                      )}
                    </div>

                    {uploadError && (
                      <div className="error-message">
                        {uploadError}
                      </div>
                    )}

                    <button
                      className="upload-btn"
                      onClick={handleUpload}
                      disabled={!selectedFile}
                    >
                      Upload
                    </button>
                  </>
                ) : uploadStatus === 'uploading' ? (
                  <div className="upload-progress">
                    <div className="spinner"></div>
                    <p>Processing CSV file...</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Show preview table with clean background */}
            {uploadStatus === 'success' && (
              <div className="preview-container">
                {processingStatus === 'idle' ? (
                    <>
                      <div className="preview-section">
                        <h3>Preview & Edit Your Data</h3>

                        <div className={`validation-summary ${hasErrors() ? 'has-errors' : 'all-valid'}`}>
                          <h4>{hasErrors() ? 'Validation Errors Found' : 'All Data Validated'}</h4>
                          <p>
                            {hasErrors()
                              ? 'Please fix the highlighted errors below before processing. Fields in red need attention.'
                              : `${rows.length} rows ready to process. Review your data and click "Process Devices" when ready.`
                            }
                          </p>
                        </div>

                        <div className="preview-table-container">
                          <table className="preview-table">
                            <thead>
                              <tr className="bulk-apply-row">
                                <th className="row-number"></th>
                                <th></th>
                                <th></th>
                                <th>
                                  <div className="bulk-apply-cell">
                                    <select
                                      value={bulkSimProvider}
                                      onChange={(e) => setBulkSimProvider(e.target.value)}
                                      className="bulk-apply-select"
                                    >
                                      <option value="">Apply to all...</option>
                                      {validOptions?.simProviders.map(sp => (
                                        <option key={sp} value={sp}>{sp}</option>
                                      ))}
                                    </select>
                                    {bulkSimProvider && (
                                      <button
                                        className="bulk-apply-btn"
                                        onClick={applyBulkSimProvider}
                                        title="Apply to all rows"
                                      >
                                        Apply
                                      </button>
                                    )}
                                  </div>
                                </th>
                                <th></th>
                                <th></th>
                                <th>
                                  <div className="bulk-apply-cell">
                                    <select
                                      value={bulkDeviceType}
                                      onChange={(e) => setBulkDeviceType(e.target.value)}
                                      className="bulk-apply-select"
                                    >
                                      <option value="">Apply to all...</option>
                                      {validOptions?.deviceTypes.map(dt => (
                                        <option key={dt} value={dt}>{dt}</option>
                                      ))}
                                    </select>
                                    {bulkDeviceType && (
                                      <button
                                        className="bulk-apply-btn"
                                        onClick={applyBulkDeviceType}
                                        title="Apply to all rows"
                                      >
                                        Apply
                                      </button>
                                    )}
                                  </div>
                                </th>
                                <th>
                                  <div className="bulk-apply-cell">
                                    <select
                                      value={bulkChannelPartner}
                                      onChange={(e) => setBulkChannelPartner(e.target.value)}
                                      className="bulk-apply-select"
                                    >
                                      <option value="">Apply to all...</option>
                                      {validOptions?.partners.map(cp => (
                                        <option key={cp} value={cp}>{cp}</option>
                                      ))}
                                    </select>
                                    {bulkChannelPartner && (
                                      <button
                                        className="bulk-apply-btn"
                                        onClick={applyBulkChannelPartner}
                                        title="Apply to all rows"
                                      >
                                        Apply
                                      </button>
                                    )}
                                  </div>
                                </th>
                                <th></th>
                              </tr>
                              <tr>
                                <th className="row-number">#</th>
                                <th>SIM Serial Number</th>
                                <th>Phone Number</th>
                                <th>SIM Provider</th>
                                <th>Device Serial Number <span className="required-field">*</span></th>
                                <th>Device IMEI Number <span className="required-field">*</span></th>
                                <th>Device Type <span className="required-field">*</span></th>
                                <th>Channel Partner <span className="required-field">*</span></th>
                                <th>Issue Date <span className="required-field">*</span></th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((row, rowIndex) => {
                                const errors = rowErrors[rowIndex] || {}
                                return (
                                  <tr key={rowIndex}>
                                    <td className="row-number">{rowIndex + 1}</td>
                                    <td className={errors.simSerialNumber ? 'error-cell' : ''}>
                                      <input
                                        type="text"
                                        value={row.simSerialNumber}
                                        onChange={(e) => updateCell(rowIndex, 'simSerialNumber', e.target.value)}
                                        className={errors.simSerialNumber ? 'error' : ''}
                                      />
                                      {errors.simSerialNumber && <div className="error-tooltip">{errors.simSerialNumber}</div>}
                                    </td>
                                    <td className={errors.phoneNumber ? 'error-cell' : ''}>
                                      <input
                                        type="text"
                                        value={row.phoneNumber}
                                        onChange={(e) => updateCell(rowIndex, 'phoneNumber', e.target.value)}
                                        className={errors.phoneNumber ? 'error' : ''}
                                      />
                                      {errors.phoneNumber && <div className="error-tooltip">{errors.phoneNumber}</div>}
                                    </td>
                                    <td className={errors.simProvider ? 'error-cell' : ''}>
                                      <select
                                        value={row.simProvider}
                                        onChange={(e) => updateCell(rowIndex, 'simProvider', e.target.value)}
                                        className={errors.simProvider ? 'error' : ''}
                                      >
                                        <option value="">Select...</option>
                                        {validOptions?.simProviders.map(sp => (
                                          <option key={sp} value={sp}>{sp}</option>
                                        ))}
                                        {row.simProvider && !validOptions?.simProviders.includes(row.simProvider) && (
                                          <option value={row.simProvider}>{row.simProvider} (custom)</option>
                                        )}
                                      </select>
                                      {errors.simProvider && <div className="error-tooltip">{errors.simProvider}</div>}
                                    </td>
                                    <td className={errors.deviceSerialNumber ? 'error-cell' : ''}>
                                      <input
                                        type="text"
                                        value={row.deviceSerialNumber}
                                        onChange={(e) => updateCell(rowIndex, 'deviceSerialNumber', e.target.value)}
                                        className={errors.deviceSerialNumber ? 'error' : ''}
                                      />
                                      {errors.deviceSerialNumber && <div className="error-tooltip">{errors.deviceSerialNumber}</div>}
                                    </td>
                                    <td className={errors.deviceImeiNumber ? 'error-cell' : ''}>
                                      <input
                                        type="text"
                                        value={row.deviceImeiNumber}
                                        onChange={(e) => updateCell(rowIndex, 'deviceImeiNumber', e.target.value)}
                                        className={errors.deviceImeiNumber ? 'error' : ''}
                                      />
                                      {errors.deviceImeiNumber && <div className="error-tooltip">{errors.deviceImeiNumber}</div>}
                                    </td>
                                    <td className={errors.deviceType ? 'error-cell' : ''}>
                                      <select
                                        value={row.deviceType}
                                        onChange={(e) => updateCell(rowIndex, 'deviceType', e.target.value)}
                                        className={errors.deviceType ? 'error' : ''}
                                      >
                                        <option value="">Select...</option>
                                        {validOptions?.deviceTypes.map(dt => (
                                          <option key={dt} value={dt}>{dt}</option>
                                        ))}
                                        {row.deviceType && !validOptions?.deviceTypes.includes(row.deviceType) && (
                                          <option value={row.deviceType}>{row.deviceType} (custom)</option>
                                        )}
                                      </select>
                                      {errors.deviceType && <div className="error-tooltip">{errors.deviceType}</div>}
                                    </td>
                                    <td className={errors.channelPartner ? 'error-cell' : ''}>
                                      <select
                                        value={row.channelPartner}
                                        onChange={(e) => updateCell(rowIndex, 'channelPartner', e.target.value)}
                                        className={errors.channelPartner ? 'error' : ''}
                                      >
                                        <option value="">Select...</option>
                                        {validOptions?.partners.map(cp => (
                                          <option key={cp} value={cp}>{cp}</option>
                                        ))}
                                      </select>
                                      {errors.channelPartner && <div className="error-tooltip">{errors.channelPartner}</div>}
                                    </td>
                                    <td className={errors.issueDate ? 'error-cell' : ''}>
                                      <DateInput
                                        value={formatDateToISO(row.issueDate)}
                                        onChange={(isoDate) => {
                                          const ukDate = formatDateToUK(isoDate)
                                          updateCell(rowIndex, 'issueDate', ukDate)
                                        }}
                                        className={errors.issueDate ? 'error' : ''}
                                      />
                                      {errors.issueDate && <div className="error-tooltip">{errors.issueDate}</div>}
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Cancel
                        </button>
                        <button
                          className="process-btn"
                          onClick={handleProcessDevices}
                          disabled={hasErrors()}
                        >
                          Process Devices
                        </button>
                      </div>
                    </>
                  ) : processingStatus === 'processing' ? (
                    <div className="processing-section">
                      <div className="spinner"></div>
                      <h3>Processing Device Upload</h3>
                      <p>Creating devices from the CSV file...</p>
                    </div>
                  ) : processingStatus === 'completed' ? (
                    <div className="processing-results">
                      <h3>Device Upload Complete</h3>
                      <p>Successfully processed all {processingResults?.totalRows || 0} rows from the CSV file.</p>
                      <p>All devices have been created.</p>

                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Process Another File
                        </button>
                      </div>
                    </div>
                  ) : processingStatus === 'error' ? (
                    <div className="processing-error">
                      <h3>Upload Unsuccessful</h3>
                      <p>The upload failed and no changes were made.</p>
                      <div className="error-message">
                        {processingError}
                        {processingResults && processingResults.failedRow > 0 && (
                          <div className="failed-row-info">
                            Failed at row {processingResults.failedRow} of {processingResults.totalRows}
                          </div>
                        )}
                      </div>
                      <div className="success-actions">
                        <button className="reset-btn" onClick={resetUpload}>
                          Try Again
                        </button>
                      </div>
                    </div>
                  ) : null}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
